import { useEffect, useState } from 'react'
import type { IntegrationResponse, ProviderResponse } from '@/shared/api/types'
import { listIntegrations, listProviders } from './api'
import { providerLabel, sortProviderCodes } from './providers'

/**
 * The integrations the sidebar lists.
 *
 * PollSphere's sidebar names every integration under one group — M-Pesa SDP,
 * IEBC API, SMS Gateway, Meta WhatsApp BSP — so you reach a provider by clicking
 * its name, not by opening a table and hunting a row. This reproduces that,
 * against a registry that is dynamic rather than hardcoded.
 *
 * Two rules make that work with **zero seed data**:
 *
 *  1. **The catalogue is the list, not the registry.** Every provider the
 *     backend knows about appears, in a fixed order, whether or not anyone has
 *     configured it. On a fresh install the group is populated and the product's
 *     shape is visible — the same reason `nav.ts` keeps unbuilt items dimmed
 *     with their phase number rather than hiding them.
 *  2. **Registered rows win.** A provider that has been registered contributes
 *     one entry per registered integration, under its own display name, because
 *     M-Pesa in sandbox and M-Pesa in production are two different things you
 *     need to tell apart. Only a provider with no registrations at all falls
 *     back to a single catalogue entry.
 *
 * Unregistered entries are dimmed but **clickable** — they lead to a page that
 * explains what the provider does and offers to register it. Dimmed-and-dead is
 * for a feature that does not exist yet; this one exists and is one click from
 * working, and making it unclickable would hide the only route to turning it on.
 */

export interface IntegrationNavEntry {
  /** Stable per row — the integration id, or `provider:CODE` when unregistered. */
  key: string
  label: string
  to: string
  registered: boolean
  /** Shown as the row badge: the environment, or DRAFT before it goes live. */
  badge?: string
  /** Drive the status dot and the row's tooltip. Absent when unregistered. */
  health?: string
  status?: string
}

export interface IntegrationNavState {
  entries: IntegrationNavEntry[]
  loading: boolean
  /** Set when the catalogue could not be read at all — the group says so. */
  error: boolean
}

// ── refresh ────────────────────────────────────────────────────────────────
// The sidebar outlives every page, so registering an integration on one screen
// has to reach a component that will not re-render on its own. A module-level
// listener set is enough — this is one list, refreshed a handful of times in a
// session, and a store library would be more machinery than the problem.

type Listener = () => void
const listeners = new Set<Listener>()

/** Call after anything that changes which integrations exist. */
export function notifyIntegrationsChanged() {
  for (const l of listeners) l()
}

export function useIntegrationNavEntries(enabled: boolean): IntegrationNavState {
  const [state, setState] = useState<IntegrationNavState>({
    entries: [], loading: true, error: false,
  })

  useEffect(() => {
    if (!enabled) { setState({ entries: [], loading: false, error: false }); return }
    let alive = true
    // Two refreshes in quick succession can land out of order; only the newest
    // may write. Without this, registering twice in a row can leave the sidebar
    // showing the state before the second registration.
    let seq = 0

    const load = async () => {
      const mine = ++seq
      setState((s) => ({ ...s, loading: true }))
      // Independently caught, not `Promise.all`-rejected: a catalogue that loads
      // while the registry 500s should still give a usable sidebar. The registry
      // has 500'd before.
      let catalogueFailed = false
      const [providers, integrations] = await Promise.all([
        listProviders().catch(() => { catalogueFailed = true; return [] as ProviderResponse[] }),
        listIntegrations().catch(() => [] as IntegrationResponse[]),
      ])
      if (!alive || seq !== mine) return
      setState({
        entries: build(providers, integrations),
        loading: false,
        // Only a total failure is worth saying out loud. A catalogue that loaded
        // still names every provider, which is the useful part.
        error: catalogueFailed && integrations.length === 0,
      })
    }

    void load()
    const listener = () => { void load() }
    listeners.add(listener)
    return () => { alive = false; listeners.delete(listener) }
  }, [enabled])

  return state
}

/**
 * The one word a row can afford.
 *
 * `DRAFT` outranks the environment: an integration that has been registered but
 * has no credentials looks, in every other respect, exactly like a working one —
 * same dot, same label — and "why is nothing coming through" is a much more
 * expensive question than "which environment is this".
 *
 * Otherwise the environment shows when it is not the ordinary case, and always
 * when a provider has more than one registration, because two identically named
 * rows with no distinguishing mark is precisely what listing per-integration was
 * meant to avoid.
 */
function rowBadge(i: IntegrationResponse, providerHasSiblings: boolean): string | undefined {
  if (i.status === 'DRAFT') return 'DRAFT'
  if (!i.environment) return undefined
  if (providerHasSiblings) return i.environment
  return i.environment === 'PRODUCTION' ? undefined : i.environment
}

export function build(
  providers: ProviderResponse[],
  integrations: IntegrationResponse[],
): IntegrationNavEntry[] {
  const byProvider = new Map<string, IntegrationResponse[]>()
  for (const i of integrations) {
    const list = byProvider.get(i.provider) ?? []
    list.push(i)
    byProvider.set(i.provider, list)
  }

  // A registered integration whose provider is missing from the catalogue must
  // still be listed — otherwise a provider retired from the catalogue silently
  // strands a live integration with no way to reach it.
  const codes = sortProviderCodes([
    ...new Set([...providers.map((p) => p.provider), ...byProvider.keys()]),
  ])

  const out: IntegrationNavEntry[] = []
  for (const code of codes) {
    const registered = byProvider.get(code) ?? []
    if (registered.length === 0) {
      out.push({
        key: `provider:${code}`,
        label: providerLabel(code),
        // Encoded: provider codes are enum-shaped today, but a future one
        // carrying `#`, `?` or `%` would silently produce a broken link.
        to: `/integrations/provider/${encodeURIComponent(code)}`,
        registered: false,
      })
      continue
    }
    // Deterministic within a provider too — two sandboxes must not swap places.
    const sorted = [...registered].sort((a, b) =>
      (a.displayName ?? '').localeCompare(b.displayName ?? '') || a.id.localeCompare(b.id))
    for (const i of sorted) {
      out.push({
        key: i.id,
        label: i.displayName || providerLabel(code),
        to: `/integrations/${i.id}`,
        registered: true,
        badge: rowBadge(i, registered.length > 1),
        health: i.health,
        status: i.status,
      })
    }
  }
  return out
}
